import { isMobileQuery } from "../helpers"

const START_CENTER = [174.7762, -41.2865] // Wellington
const START_ZOOM = 9
const KEY_MAX_VISIBLE_ITEMS = 5
const DETAIL_ZOOM_LEVEL = 10

export class GwMap {
  /* global mapboxgl */
  constructor(element, clusterMarkers = false) {
    this.element = element
    this.clusterMarkers = clusterMarkers

    this.accessToken = element.dataset.accessToken
    this.map = null
    this.markers = []

    this.initMapData()

    if (this.hasMapKey()) {
      this.initMapKey(isMobileQuery)
    }

    this.setMap()

    this.drawFeatures(this.featureCollections)
    this.centerMap()

    this.bindWindowEvents()
    this.bindMapEvents()

    this.map.once("idle", () => {
      document.querySelector("#map canvas").setAttribute("tabindex", -1)

      document.querySelectorAll(".mapboxgl-ctrl button").forEach(btnCtrl => {
        btnCtrl.setAttribute("tabindex", -1)
      })

      document.querySelectorAll(".mapboxgl-ctrl a").forEach(btnCtrl => {
        btnCtrl.setAttribute("tabindex", -1)
      })
    })
  }

  hasMapKey() {
    return !!this.element.querySelector(".map-key")
  }

  initMapData() {
    this.mapData = JSON.parse(this.element.dataset.mapboxData)

    // filter out items with no geojson source
    this.featureCollections = this.mapData.items
      .map(item => item.geojson)
      .filter(g => !!g)
      .map(geojson => {
        geojson.features = geojson.features.map(feature => {
          if (feature.geometry.rings) {
            feature.type = "Feature"
            feature.geometry = {
              type: "Polygon",
              coordinates: feature.geometry.rings,
            }
          }

          // flatten style properties to access them when adding layers
          if (feature.properties.style) {
            feature.properties = { ...feature.properties, ...feature.properties.style }
          }

          return feature
        })

        return geojson
      })

    this.activeCollectionsIDs = this.featureCollections.map(collection => collection.id)
  }

  initMapKey(mobileQuery) {
    const mapKey = this.element.querySelector(".map-key")
    const heading = mapKey.querySelector(".map-key__heading")
    const collapse = mapKey.querySelector(".map-key__collapse")
    const container = mapKey.querySelector(".map-key__items")
    const items = container.querySelectorAll(".map-key__item")

    const showCollapse = mobileQuery.matches || items.length > KEY_MAX_VISIBLE_ITEMS

    heading.classList.toggle("d-none", showCollapse)
    collapse.classList.toggle("d-none", !showCollapse)
    container.classList.toggle("collapse", showCollapse)
  }

  setMap() {
    mapboxgl.accessToken = this.accessToken
    this.map = new mapboxgl.Map({
      container: this.element.id, // container id
      style: "mapbox://styles/somar-design-studios/ck7v96zf6002i1imo1qv689cc", // stylesheet location
      center: START_CENTER, // starting position [lng, lat]
      zoom: START_ZOOM, // starting zoom
    })

    // disable zoom on scroll
    this.map.scrollZoom.disable()

    this.map.addControl(new mapboxgl.FullscreenControl())

    // Add zoom and rotation controls to the map.
    this.map.addControl(new mapboxgl.NavigationControl())
  }

  drawFeatures(featureCollections) {
    this.map.on("load", () => {
      featureCollections.forEach(featureCollection => {
        this.map.addSource(`features-${featureCollection.id}`, {
          type: "geojson",
          data: featureCollection,
        })

        // Add a layer for polygons
        this.map.addLayer({
          id: `layer-polygons-${featureCollection.id}`,
          type: "fill",
          source: `features-${featureCollection.id}`,
          paint: {
            "fill-color": { type: "identity", property: "color" },
            "fill-opacity": 0.5,
          },
          filter: ["==", "$type", "Polygon"],
        })

        // Add a layer for lines
        this.map.addLayer({
          id: `layer-lines-${featureCollection.id}`,
          type: "line",
          source: `features-${featureCollection.id}`,
          paint: {
            "line-color": { type: "identity", property: "color" },
            "line-width": 6,
            "line-dasharray": [2, 1],
          },
          filter: ["==", "$type", "LineString"],
        })
      })

      // Add a layers for markers
      this.drawMarkers(featureCollections)
    })
  }

  drawMarkers(featureCollections) {
    const allPoints = featureCollections.reduce(
      (features, collection) =>
        features.concat(collection.features.filter(feature => feature.geometry.type === "Point")),
      []
    )

    let sourceOptions = {}

    if (this.clusterMarkers) {
      sourceOptions = { cluster: true, clusterMaxZoom: 17, clusterRadius: 60 }
    }

    this.map.addSource("points", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: allPoints,
      },
      ...sourceOptions,
    })

    this.map.addLayer({
      id: "layer-points",
      type: "symbol",
      source: "points",
      filter: ["!", ["has", "point_count"]],
      layout: {
        "icon-image": "{icon}",
        "icon-size": 1,
        "icon-anchor": "bottom",
        "icon-allow-overlap": true,
      },
      paint: {
        "text-color": "#175a5b",
        // set opacity to 0 when zoom is < DETAIL_ZOOM_LEVEL for points that have properties.detail_view_only == true
        "icon-opacity": [
          "step",
          ["zoom"],
          ["case", ["==", true, ["get", "detail_view_only"]], 0, 1],
          DETAIL_ZOOM_LEVEL,
          1,
        ],
      },
    })

    if (this.clusterMarkers) {
      this.map.addLayer({
        id: "layer-points-clusters",
        type: "symbol",
        source: "points",
        filter: ["has", "point_count"],
        layout: {
          "text-field": "{point_count_abbreviated}",
          "text-font": ["Source Sans Pro Bold", "Arial Unicode MS Bold"],
          "text-size": 12,
          "text-offset": [0, -0.5],
          "icon-image": "marker-cluster",
          "icon-size": 1,
        },
        paint: {
          "text-color": "#103541",
        },
      })
    }
  }

  bindWindowEvents() {
    isMobileQuery.addEventListener("change", this.initMapKey.bind(this))
  }

  bindMapEvents() {
    const setPointer = pointerStyle => (this.map.getCanvas().style.cursor = pointerStyle)
    const normalizeCoordinates = (lng, coordinates) => {
      let normalized = coordinates.slice()
      // Ensure that if the map is zoomed out such that multiple
      // copies of the feature are visible, the popup appears
      // over the copy being pointed to.
      while (Math.abs(lng - normalized[0]) > 180) {
        normalized[0] += lng > normalized[0] ? 360 : -360
      }

      return normalized
    }

    const basePopupProperties = {
      offset: {
        top: [0, 0],
        bottom: [0, -35],
        left: [16, -18],
        right: [-16, -18],
        "bottom-left": [16, 0],
        "bottom-right": [-16, 0],
      },
      keepInView: true,
    }

    let hoverPopup, clickPopup

    this.map.on("mouseenter", "layer-points", e => {
      const feature = this.findVisibleFeature(e.features)

      if (!feature) {
        return
      }

      if (clickPopup && clickPopup.featureID === feature.id) {
        return
      }

      setPointer("pointer")

      const coordinates = normalizeCoordinates(e.lngLat.lng, feature.geometry.coordinates)

      hoverPopup = new mapboxgl.Popup({
        ...basePopupProperties,
        closeButton: false,
        closeOnClick: false,
      })
        .setLngLat(coordinates)
        .setHTML(`<h3>${feature.properties.title}</h3>`)

      hoverPopup.addTo(this.map)
    })

    this.map.on("mouseleave", "layer-points", () => {
      setPointer("")

      if (hoverPopup) {
        hoverPopup.remove()
        hoverPopup = null
      }
    })

    this.map.on("click", "layer-points", e => {
      const feature = this.findVisibleFeature(e.features)

      if (!feature) {
        return
      }

      const coordinates = normalizeCoordinates(e.lngLat.lng, feature.geometry.coordinates)
      const properties = feature.properties

      clickPopup = new mapboxgl.Popup(basePopupProperties).setLngLat(coordinates).setHTML(
        `<h3>${properties.title}</h3>
          ${properties.description}
          ${properties.link ? properties.link : ""}`
      )

      clickPopup.featureID = feature.id

      clickPopup.on("close", () => {
        if (clickPopup.featureID === feature.id) {
          clickPopup = null
        }
      })

      if (hoverPopup) {
        hoverPopup.remove()
        hoverPopup = null
      }

      clickPopup.addTo(this.map)
    })

    if (this.clusterMarkers) {
      this.map.on("mouseenter", "layer-points-clusters", () => {
        setPointer("pointer")
      })

      this.map.on("mouseleave", "layer-points-clusters", () => {
        setPointer("")
      })

      // ease on a cluster click
      this.map.on("click", "layer-points-clusters", e => {
        var features = this.map.queryRenderedFeatures(e.point, {
          layers: ["layer-points-clusters"],
        })

        var clusterId = features[0].properties.cluster_id

        this.map.getSource("points").getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) {
            return
          }

          this.map.easeTo({
            center: features[0].geometry.coordinates,
            zoom: zoom,
          })
        })
      })
    }
  }

  isFeatureHidden(feature) {
    return feature.properties.detail_view_only && this.map.getZoom() < DETAIL_ZOOM_LEVEL
  }

  findVisibleFeature(features) {
    return features.find(feature => !this.isFeatureHidden(feature))
  }

  getVisibleCollections() {
    return this.featureCollections.filter(collection => this.activeCollectionsIDs.includes(collection.id))
  }

  getVisibleFeatures() {
    return this.getVisibleCollections().reduce((features, collection) => features.concat(collection.features), [])
  }

  updateFeaturesVisibility(recenter = false) {
    this.featureCollections.forEach(featureCollection => {
      const visible = this.activeCollectionsIDs.includes(featureCollection.id)

      this.map.setLayoutProperty(`layer-polygons-${featureCollection.id}`, "visibility", visible ? "visible" : "none")
      this.map.setLayoutProperty(`layer-lines-${featureCollection.id}`, "visibility", visible ? "visible" : "none")
    })

    const allVisiblePoints = this.getVisibleCollections().reduce(
      (features, collection) =>
        features.concat(collection.features.filter(feature => feature.geometry.type === "Point")),
      []
    )

    this.map.getSource("points").setData({
      type: "FeatureCollection",
      features: allVisiblePoints,
    })

    if (recenter) {
      this.centerMap()
    }
  }

  centerMap() {
    const visibleFeatures = this.getVisibleFeatures()

    if (visibleFeatures.length) {
      // get boundaries including all visible features
      const bounds = this.getVisibleFeatures().reduce((bounds, feature) => {
        if (feature.geometry.type === "Point") {
          return bounds.extend(feature.geometry.coordinates)
        } else {
          if (feature.geometry.coordinates) {
            return feature.geometry.coordinates[0].reduce((bounds, coordinates) => bounds.extend(coordinates), bounds)
          } else {
            return bounds
          }
        }
      }, new mapboxgl.LngLatBounds())

      try {
        this.map.fitBounds(bounds, {
          padding: {
            top: 15,
            bottom: 15,
            left: 15,
            right: 15,
          },
        })
      } catch (error) {
        // map fit bounds error
      }
    } else {
      this.map.flyTo({ START_CENTER })
    }
  }
}

export default function init(pageClass) {
  if (document.querySelector(`.${pageClass}`)) {
    const mapElement = document.getElementById("map")

    if (mapElement) {
      new GwMap(mapElement)
    }
  }
}
